import { Flex, GridItem, SimpleGrid } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as ReactRouterLink } from "react-router";

import {
  ButtonWrapper,
  FormDerivationRulesFor,
  RanaButton,
  RanaHeading,
  RanaLoading,
} from "@nens/ranalib";
import { useFrontendSetting } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";

import { RanaFormMapInput } from "@/lib/rana-form-map-input";
import { cancelButtonText } from "@/translations";

import { useTenantNewProject } from "./hooks/use-tenant-new-project";
import { ProjectFormValues } from "./types/project-form-values";
import { MAX_CHAR_CODE, MAX_CHAR_NAME } from "./utils/project-consts";

const INITIAL_VALUES: ProjectFormValues = {
  name: "",
  code: "",
  description: "",
  location: null,
};

const slugify = (text: string) => {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove non-word chars (except spaces and hyphens)
    .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
    .replace(/--+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
};

const PROJECT_DERIVATION_RULES: FormDerivationRulesFor<ProjectFormValues> = {
  code: (data) => slugify(data.name),
};

export function TenantNewProject() {
  const intl = useIntl();

  const { maptilerApiKey, isSuccess } = useFrontendSetting();

  const { validate, submit, isMutating, projectListUrl } = useTenantNewProject();

  if (!isSuccess) return null;

  return (
    <>
      <RanaHeading type="h3">
        <FormattedMessage
          id="newproject.general_information"
          defaultMessage="General information"
        />
      </RanaHeading>
      <RanaForm
        initialValues={INITIAL_VALUES}
        validate={validate}
        doSubmit={submit}
        derivationRules={PROJECT_DERIVATION_RULES}
      >
        <SimpleGrid gap={4} columns={{ base: 1, md: 2 }} marginBottom={8}>
          <GridItem>
            <Flex direction="column">
              <RanaForm.Input
                field="name"
                isRequired
                label={intl.formatMessage({
                  id: "newproject.name.label",
                  defaultMessage: "Name",
                })}
                maxChar={MAX_CHAR_NAME}
                explanation={intl.formatMessage({
                  id: "newproject.name.explanation",
                  defaultMessage: "Type in a project name of max 128 characters",
                })}
              />
              <RanaForm.Input
                field="code"
                isRequired
                label={intl.formatMessage({
                  id: "newproject.code.label",
                  defaultMessage: "Code",
                })}
                explanation={intl.formatMessage({
                  id: "newproject.code.explanation",
                  defaultMessage: "Type in a project code of max 32 characters",
                })}
                maxChar={MAX_CHAR_CODE}
              />
              <RanaForm.Textarea
                field="description"
                label={intl.formatMessage({
                  id: "newproject.description.label",
                  defaultMessage: "Description",
                })}
                explanation={intl.formatMessage({
                  id: "newproject.description.explanation",
                  defaultMessage:
                    "Type in a project description, for example the objective of the project.",
                })}
                h={52}
                w="full"
              />
              <RanaForm.GeocoderInput
                field="location"
                maptilerApiKey={maptilerApiKey}
                label={intl.formatMessage({
                  id: "newproject.geocoder.label",
                  defaultMessage: "Location",
                })}
                explanation={intl.formatMessage({
                  id: "newproject.geocoder.explanation",
                  defaultMessage: "Search a location by name or left-click on the map to mark one",
                })}
              />
            </Flex>
          </GridItem>
          <GridItem minH="md">
            <RanaFormMapInput
              field="location"
              label={intl.formatMessage({
                id: "newproject.mapinput.label",
                defaultMessage: "Left-click on the map to mark a location",
              })}
              mapId="newprojectlocationmap"
              maptilerApiKey={maptilerApiKey}
            />
          </GridItem>
        </SimpleGrid>
        <RanaForm.Errors />

        <Flex direction="row" w="full" mt={4} justifyContent="flex-end">
          <ButtonWrapper>
            <RanaButton as={ReactRouterLink} to={projectListUrl} variant="tertiary">
              {cancelButtonText}
            </RanaButton>
            {isMutating ? <RanaLoading /> : null}
            <RanaForm.SubmitButton isDisabled={isMutating}>
              <FormattedMessage id="newproject.create_button" defaultMessage="Create project" />
            </RanaForm.SubmitButton>
          </ButtonWrapper>
        </Flex>
      </RanaForm>
    </>
  );
}
